import { useQueryClient } from "@tanstack/react-query";
import type { ShopDto, ShopReasonsDto } from "../generated/schema";
import { useApiMutation } from "./use-api-mutation";
import { invalidateShopQueryKey } from "./use-invalidate-shop";

export default function useUpdateShopReasons(shopId: string) {
  const queryClient = useQueryClient();

  return useApiMutation("patch", "/shops/{shopId}", {
    notify: false,
    onMutate: async (newReasons: ShopReasonsDto[]) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: invalidateShopQueryKey(shopId),
      });

      // Snapshot the previous value
      const previousShop = queryClient.getQueryData<ShopDto>(
        invalidateShopQueryKey(shopId),
      );

      // Optimistically update to the new value
      queryClient.setQueryData<ShopDto>(
        invalidateShopQueryKey(shopId),
        (old) => {
          if (!old) return old;
          return { ...old, reasons: newReasons };
        },
      );

      // Return a context object with the snapshotted value
      return { previousShop };
    },
    onError: (_err, _newReasons, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      const ctx = context as { previousShop?: ShopDto } | undefined;
      if (ctx?.previousShop) {
        queryClient.setQueryData(
          invalidateShopQueryKey(shopId),
          ctx.previousShop,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we're in sync with the server
      queryClient.invalidateQueries({
        queryKey: invalidateShopQueryKey(shopId),
      });
    },
    mapVariables: (reasons: ShopReasonsDto[]) => ({
      params: { path: { shopId } },
      body: { reasons },
    }),
  });
}
